import type { ReactNode } from 'react';
import { Building2, CalendarClock, ChevronRight, Inbox, Users } from 'lucide-react';
import type { RequisitionDto } from '@eombor/contracts';
import { Badge, Button, Drawer, EmptyState, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';

/** Sana: «07.09.2026». Muddat kun aniqligida — vaqt ko'rsatilmaydi. */
const formatDate = (iso: string | null): string | null => {
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

/** Kartochkadagi bitta qator: ikonka + yorliq + qiymat. */
const Meta = ({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) => (
  <span className="flex min-w-0 items-start gap-1.5 text-xs leading-5">
    <span aria-hidden className="mt-0.5 shrink-0 text-text-faint">
      {icon}
    </span>
    <span className="shrink-0 text-text-faint">{label}</span>
    <span className="min-w-0 flex-1 break-words text-text-muted">{children}</span>
  </span>
);

/**
 * Navbatdagi bitta talabnoma — BUTUNLAY bosiladigan kartochka.
 *
 * Qabulning O'ZI bu yerda bajarilmaydi. Server tasdiq uchun rasm bilan
 * dalolatnoma talab qiladi («Qabulni tasdiqlash uchun rasm bilan
 * dalolatnoma kerak»), rasm yuklash esa talabnoma sahifasida turadi.
 * Modal ichida bo'sh so'rov yuborish faqat xato toastini keltirardi.
 *
 * Shuning uchun kartochka bitta narsa qiladi: kerakli sahifaga olib
 * boradi. Buning IKKI yo'li bor va ikkovi ham ayni ishni bajaradi —
 * pastdagi to'liq kenglikdagi tugma (aniq nishon) va kartochkaning
 * butun yuzasi (shoshgan barmoq uchun).
 */
const AcceptRow = ({
  row,
  warehouseName,
  onOpen,
}: {
  row: RequisitionDto;
  warehouseName: string;
  onOpen: (row: RequisitionDto) => void;
}) => {
  const needed = formatDate(row.neededBy);

  const open = (): void => onOpen(row);

  return (
    <li
      className={cn(
        'relative flex flex-col gap-3 rounded-[var(--radius-md)] border border-border',
        'bg-surface p-3 shadow-[var(--shadow-1)]',
        'transition-[background-color,border-color] duration-[var(--motion-fast)]',
        'ease-[var(--ease-standard)] hover:border-accent/30 hover:bg-accent-soft/40',
        'motion-reduce:transition-none',
      )}
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {/*
            Hujjat raqami — kartochka yuzasining bosish nishoni: uning
            `::after` i butun kartochkani qoplaydi. Kartochkaning O'ZINI
            `<button>` qilib bo'lmaydi, chunki ichida yana bir tugma bor
            va tugma ichidagi tugma haqiqiy HTML emas.
          */}
          <button
            type="button"
            onClick={open}
            aria-label={t('accept.openFor', { docNo: row.docNo })}
            className={cn(
              'tabular text-sm font-semibold text-text',
              "after:absolute after:inset-0 after:content-['']",
              'after:rounded-[var(--radius-md)]',
            )}
          >
            {row.docNo}
          </button>
          <Badge tone="info">{t('accept.statusHandedOver')}</Badge>
        </span>

        {/* SABAB — kartochkaning asosiy matni: odam aynan shu bo'yicha
            «bu qaysi buyurtma edi?» degan savolga javob topadi. */}
        <p className="line-clamp-2 text-sm font-medium text-pretty text-text">{row.purpose}</p>

        <Meta icon={<Building2 size={13} />} label={t('requisition.colWarehouse')}>
          {warehouseName}
        </Meta>

        {needed === null ? null : (
          <Meta icon={<CalendarClock size={13} />} label={t('requisition.colNeededBy')}>
            <span className="tabular">{needed}</span>
          </Meta>
        )}

        {row.throughPerson === null ? null : (
          <Meta icon={<Users size={13} />} label={t('requisition.fieldThroughPerson')}>
            {row.throughPerson}
          </Meta>
        )}
      </div>

      {/*
        Aniq nishon. Ilgari bu yerda faqat kichkina strelka turardi va u
        barmoq uchun ham kichik, ma'no jihatidan ham jim edi — «bu yerda
        nima bo'ladi?» degan savolga javob bermasdi.

        `relative z-[1]` — hujjat raqamining butun kartochkani qoplagan
        `::after` i USTIDA turadi. Shu sababli tugma bosilganda kartochka
        nishoni ishlamaydi va hodisa ikki marta ketmaydi.

        `size="lg"` = `--size-control` = 44px — tokenning o'zi buni
        «minimal teginish nishoni» deb ataydi.
      */}
      <Button variant="accent" size="lg" onClick={open} className="relative z-[1] w-full">
        {t('accept.action')}
        <ChevronRight size={16} aria-hidden />
      </Button>
    </li>
  );
};

export interface AcceptSheetProps {
  open: boolean;
  onClose: () => void;
  rows: readonly RequisitionDto[];
  /** Ombor nomi identifikator o'rniga — nomlar boshqa so'rovdan keladi. */
  warehouseNames?: ReadonlyMap<string, string>;
  pending?: boolean;
  /** Kartochka bosildi — oynani yopib, talabnoma sahifasiga o'tish. */
  onOpenRow: (row: RequisitionDto) => void;
}

/**
 * QABUL QILISH — pastdan chiquvchi ro'yxat.
 *
 * `Drawer` ning `sheet` rejimi ustiga qurilgan: u telefonda pastdan
 * ko'tariladi, ushlagichdan pastga surib yopiladi, backdrop va `Esc`
 * bilan ham yopiladi, tanani siljishdan qulflaydi, fokusni ushlab
 * turadi va `role="dialog" aria-modal` bilan e'lon qilinadi.
 *
 * Oyna hech qanday so'rov YUBORMAYDI — u faqat ro'yxat ko'rsatadi va
 * kerakli sahifaga yo'naltiradi.
 */
export const AcceptSheet = ({
  open,
  onClose,
  rows,
  warehouseNames,
  pending = false,
  onOpenRow,
}: AcceptSheetProps) => (
  <Drawer
    sheet
    open={open}
    onClose={onClose}
    title={t('accept.sheetTitle')}
    description={
      pending || rows.length === 0
        ? t('accept.sheetHint')
        : t('accept.sheetHintCount', { count: rows.length })
    }
    closeLabel={t('accept.close')}
  >
    {/* Bo'shliqni `Drawer` ning o'zi beradi — bu yerda yana `px-6`
        qo'yilsa, telefonda ikki chetdan 48px yo'qolardi. */}
    <div className="flex flex-col gap-2">
      {pending ? (
        <>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Inbox size={20} aria-hidden />}
          title={t('accept.emptyTitle')}
          description={t('accept.emptyHint')}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <AcceptRow
              key={row.id}
              row={row}
              warehouseName={warehouseNames?.get(row.warehouseId) ?? `#${row.warehouseId}`}
              onOpen={onOpenRow}
            />
          ))}
        </ul>
      )}
    </div>
  </Drawer>
);
